import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clamp } from 'utils/number';
import { EbmProposalsCell } from 'widgets/results/EbmProposalsSection.styles';
import {
  DurationInput,
  MultiplierInput,
} from 'widgets/results/EbmProposals/EbmProposalDurationInput.styles';

const MAX_MINUTES = 90;

type Props = {
  duration: number;
  onDurationChange: (duration: number) => void;
};

export function EbmProposalDurationInput({ duration, onDurationChange }: Props) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState<string>(String(duration));

  useEffect(() => {
    setInputValue(String(duration));
  }, [duration]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const typed = parseInt(e.target.value, 10);
      if (Number.isNaN(typed)) {
        setInputValue('');
        onDurationChange(duration);
        return;
      }
      if (typed > MAX_MINUTES) return;
      const parsed = clamp(typed, 1, MAX_MINUTES);
      setInputValue(String(parsed));
      onDurationChange(parsed);
    },
    [duration, onDurationChange]
  );

  const multiplier = Math.max(0, Math.floor(duration / 10));

  return (
    <>
      <EbmProposalsCell>
        <span className="text-muted-foreground">×</span>
        <MultiplierInput
          type="number"
          disabled
          value={multiplier}
          aria-label={t('result.ebmProposals.multiplierAriaLabel')}
        />
      </EbmProposalsCell>
      <EbmProposalsCell>
        <span className="text-gray-500">{t('result.ebmProposals.durationLabel')}</span>
        <DurationInput
          type="number"
          min={1}
          max={MAX_MINUTES}
          value={inputValue}
          onChange={handleInputChange}
          aria-label={t('result.ebmProposals.durationAriaLabel')}
        />
        <span className="text-gray-500">{t('result.ebmProposals.minutesShort')}</span>
      </EbmProposalsCell>
    </>
  );
}
